import type { WeatherDay, WeatherForecastRibbon } from '@interfaces/IWeatherInsights';
import { authedFetch, UnauthorizedError } from '@apis/auth';

/** Map a non-OK weather response to the right error (401 → UnauthorizedError,
 *  else the backend message or a generic fallback). Shared by both fetchers. */
async function throwWeatherError(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(body?.error?.message ?? 'Weather data is currently unavailable');
}

/**
 * Heavy AI body for a single day. `date` is optional — when omitted the backend
 * defaults to its own "today" (the site/project tz) and returns the
 * authoritative `selectedDate`. `scheduleNumber` is the user's active-schedule
 * pin — omitted means the backend resolves the latest-updated schedule; a stale
 * pin degrades the same way server-side. Equipment advisories are only
 * populated for today.
 */
export async function fetchWeatherDay(
  siteId: string,
  date?: string,
  scheduleNumber?: string,
  signal?: AbortSignal,
): Promise<WeatherDay> {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (scheduleNumber) params.set('scheduleNumber', scheduleNumber);
  const qs = params.size > 0 ? `?${params.toString()}` : '';
  const res = await authedFetch(`/api/sites/${siteId}/weather/today${qs}`, { signal });
  if (res.ok) return (await res.json()) as WeatherDay;
  return throwWeatherError(res);
}

/** Cheap day-summary ribbon (no AI). */
export async function fetchWeatherForecastRibbon(
  siteId: string,
  signal?: AbortSignal,
): Promise<WeatherForecastRibbon> {
  const res = await authedFetch(`/api/sites/${siteId}/weather/forecast`, { signal });
  if (res.ok) return (await res.json()) as WeatherForecastRibbon;
  return throwWeatherError(res);
}
